import { useState, useEffect } from 'react';
import { Sheet, Field, Input, Textarea, PrimaryBtn, GhostBtn, IconBtn, Icons } from './UI';
import { today, linkify } from '../utils/constants';
import { api } from '../utils/api';

function ShopForm({ initial, onSave, onClose }) {
  const [item, setItem] = useState(initial?.item || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!item.trim()) { alert('Item name is required.'); return; }
    setSaving(true);
    await onSave(item.trim(), notes);
    setSaving(false);
  };

  return (
    <Sheet title={initial ? 'Edit Item' : 'Add Item'} onClose={onClose} footer={<><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : initial ? 'Save Changes' : 'Add Item'}</PrimaryBtn></>}>
      <Field label="Item *">
        <Input value={item} onChange={e => setItem(e.target.value)} placeholder="What do you need?" autoFocus />
      </Field>
      <Field label="Notes / Justification">
        <Textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why do you need it? Context for the boss..." />
      </Field>
    </Sheet>
  );
}

export function ShoppingTab({ store, setStore, currentUser, newActionRef }) {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  useEffect(() => { if (newActionRef) newActionRef.current = { task: () => setShowForm(true) }; }, [newActionRef]);

  const items = store.shopItems || [];

  const addItem = async (item, notes) => {
    const newItem = { id: store.shopNextId, item, notes, purchased: false, createdAt: today(), purchasedBy: '' };
    setStore(s => ({ ...s, shopNextId: s.shopNextId + 1, shopItems: [...(s.shopItems || []), newItem] }));
    setShowForm(false);
    await api.saveShopItem(newItem);
  };

  const editItem = async (item, notes) => {
    const updated = { ...editingItem, item, notes };
    setStore(s => ({ ...s, shopItems: s.shopItems.map(i => i.id === updated.id ? updated : i) }));
    setEditingItem(null);
    await api.saveShopItem(updated);
  };

  const togglePurchased = async (id) => {
    const i = items.find(i => i.id === id);
    if (!i) return;
    const updated = { ...i, purchased: !i.purchased, purchasedBy: !i.purchased ? (currentUser || '') : '' };
    setStore(s => ({ ...s, shopItems: s.shopItems.map(x => x.id === id ? updated : x) }));
    await api.saveShopItem(updated);
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this item?')) return;
    setStore(s => ({ ...s, shopItems: s.shopItems.filter(i => i.id !== id) }));
    await api.deleteShopItem(id);
  };

  // Build torn paper SVG bottom
  const tearPath = (() => {
    let p = 'M0,0 ';
    for (let i = 0; i <= 40; i++) {
      const x = (i / 40) * 700;
      const y = 10 + Math.sin(i * 2.3) * 6 + Math.sin(i * 5.1) * 4;
      p += `L${x.toFixed(1)},${y.toFixed(1)} `;
    }
    return p + 'L700,40 L0,40 Z';
  })();

  return (
    <>
      <div style={{ background: '#fefce8', minHeight: 400, borderRadius: '8px 8px 0 0', boxShadow: '2px 2px 8px rgba(0,0,0,.1)', position: 'relative', backgroundImage: 'repeating-linear-gradient(transparent,transparent 31px,#bfdbfe 31px,#bfdbfe 32px)', backgroundPosition: '0 40px' }}>
        <div style={{ position: 'absolute', left: 48, top: 0, bottom: 0, width: 2, background: '#fca5a5', pointerEvents: 'none' }} />
        <div style={{ padding: '12px 12px 8px 56px', fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', borderBottom: '2px solid #bfdbfe', display: 'flex', gap: 0 }}>
          <span style={{ flex: 1 }}>Item</span>
          <span style={{ flex: 1.5 }}>Notes / Justification</span>
          <span style={{ width: 80 }} />
        </div>

        {items.length === 0 && (
          <div style={{ padding: '32px 56px', color: '#94a3b8', fontStyle: 'italic', fontSize: 14, lineHeight: 32 }}>Nothing on the list yet — add something!</div>
        )}

        {items.map(item => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 12px 6px 56px', minHeight: 32, gap: 12, lineHeight: '32px', opacity: item.purchased ? 0.7 : 1 }}>
            <div style={{ flex: 1, fontSize: 14, color: item.purchased ? '#ef4444' : '#1e293b', textDecoration: item.purchased ? 'line-through' : 'none', wordBreak: 'break-word', paddingTop: 6 }}>{item.item}</div>
            <div style={{ flex: 1.5, fontSize: 13, color: item.purchased ? '#fca5a5' : '#64748b', fontStyle: 'italic', textDecoration: item.purchased ? 'line-through' : 'none', wordBreak: 'break-word', paddingTop: 6 }}>
              <span dangerouslySetInnerHTML={{ __html: linkify(item.notes) }} />
              {item.purchased && item.purchasedBy && <span style={{ fontSize: 11, color: '#22c55e', fontStyle: 'normal', marginLeft: 6 }}>✓ {item.purchasedBy}</span>}
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0, paddingTop: 4 }}>
              <IconBtn onClick={() => togglePurchased(item.id)} title={item.purchased ? 'Unmark' : 'Mark Purchased'} color={item.purchased ? '#22c55e' : '#94a3b8'}>
                {item.purchased ? <Icons.Check /> : <Icons.Circle />}
              </IconBtn>
              <IconBtn onClick={() => setEditingItem(item)} title="Edit"><Icons.Edit /></IconBtn>
              <IconBtn onClick={() => deleteItem(item.id)} title="Delete" color="#ef4444"><Icons.X /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {/* Torn paper bottom */}
      <div style={{ width: '100%', height: 40, overflow: 'hidden', marginBottom: 80 }}>
        <svg viewBox="0 0 700 40" preserveAspectRatio="none" width="100%" height="40" xmlns="http://www.w3.org/2000/svg">
          <path d={tearPath} fill="#fefce8" />
        </svg>
      </div>

      {showForm && <ShopForm onSave={addItem} onClose={() => setShowForm(false)} />}
      {editingItem && <ShopForm initial={editingItem} onSave={editItem} onClose={() => setEditingItem(null)} />}
    </>
  );
}

